/**
 * WorkflowStepList.jsx
 *
 * Scrollable list that renders a WorkflowStepCard for each step in the plan.
 */

import {
    Box, Collapse, IconButton, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WorkflowStepCard from './WorkflowStepCard';
import { displayStepParams } from './stepParamOverrides';

/**
 * @param {Object} props
 * @param {Array} props.actions - Array of plan action entries
 * @param {Object} props.stepParams - { [stepIndex]: { ...paramOverrides } }.
 *   Each card is shown the plan's own params with these overrides on top, so
 *   an untouched field displays the reviewed value rather than a blank box.
 * @param {Object} props.stepValidation - { [stepIndex]: StepStatus }
 * @param {Function} props.onParamChange - (stepIndex, paramName, value) => void
 * @param {Array} props.results - Per-step execution results
 * @param {Object} props.executionStates - { [stepIndex]: { status, error } }
 * @param {Function} props.onRetry - Called when user clicks retry on a failed step
 * @param {Function} props.onSkip - Called when user clicks skip on a failed step
 * @param {React.ReactNode} [props.header] - Optional content rendered above the step cards
 * @param {boolean} [props.stepsExpanded] - whether the step cards are shown
 * @param {Function} [props.onToggleSteps] - when given, the cards sit inside one
 *   collapsible "Workflow steps" group (#146). The header stays outside it: it
 *   carries the campaign panel, whose checkpoint controls and narratives must
 *   never be collapsible.
 */
export default function WorkflowStepList({
    actions, stepParams, stepValidation, onParamChange, results, executionStates, onRetry, onSkip,
    header = null, disabled = false, stepsExpanded = true, onToggleSteps = null,
}) {
    const stepCount = (actions || []).length;
    const cards = (
        <>
            {(actions || []).map((action, i) => (
                <WorkflowStepCard
                    key={`${action.actionId}-${action.step || i}`}
                    action={action}
                    stepIndex={i}
                    params={displayStepParams(action, stepParams?.[i])}
                    validationStatus={stepValidation?.[i]}
                    onParamChange={onParamChange}
                    result={results?.[i]}
                    executionState={executionStates?.[i]}
                    onRetry={onRetry}
                    onSkip={onSkip}
                    disabled={disabled}
                />
            ))}
        </>
    );

    return (
        <Box sx={{ overflowY: 'auto' }}>
            {header}
            {onToggleSteps ? (
                <>
                    <Box
                        onClick={() => onToggleSteps(!stepsExpanded)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            userSelect: 'none',
                            px: 1.5,
                            py: 0.5,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                        }}
                        data-testid="workflow-steps-group-toggle"
                    >
                        {stepsExpanded
                            ? <ExpandLessIcon fontSize="small" sx={{ mr: 0.5 }} />
                            : <ExpandMoreIcon fontSize="small" sx={{ mr: 0.5 }} />}
                        {/* Her label, from the build she shared on 08-04. The
                            standup said "workflow steps or something"; matching
                            what she actually shipped removes a needless diff. */}
                        <Typography variant="subtitle2" fontWeight="bold">
                            Editable Workflow Steps
                        </Typography>
                        {/* The count is the whole point of a collapsed group: it
                            says how much is behind it without opening it. */}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                            {`(${stepCount})`}
                        </Typography>
                    </Box>
                    <Collapse in={stepsExpanded} data-testid="workflow-steps-group">
                        <Box sx={{ px: 1, pt: 1 }}>{cards}</Box>
                    </Collapse>
                </>
            ) : <Box sx={{ px: 1, pt: 1 }}>{cards}</Box>}
        </Box>
    );
}
